/**
 * The SPA's session + permission context — the replacement for next-auth's
 * `<SessionProvider>` plus the server work `src/app/(dashboard)/layout.tsx`
 * used to do before rendering.
 *
 * Holds three things:
 *   - the token session (`AuthSession`, ported from mobile),
 *   - the signed-in user,
 *   - the `PagePermission` map that drives the nav and the route guard.
 *
 * `status` is deliberately three-valued rather than a boolean: "loading" (the
 * stored session has not been read yet) must not look like "unauthenticated",
 * or every hard refresh would flash the login page before restoring the
 * session.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AuthSession } from "./session";
import { AuthError, type MobileUser } from "./types";
import { describeBrowser, getOrCreateDeviceId } from "./device-id";
import { disableGoogleAutoSelect } from "./google";
import { createBrowserTokenStorage } from "../storage/token-storage";
import {
  clearLegacySession,
  establishLegacySession,
  type LegacyBridgeResult,
} from "./legacy-bridge";
import {
  ADMIN_FULL_GRANT,
  normalizePermissionRows,
  visiblePageKeys as computeVisiblePageKeys,
  type PagePermissionRow,
  type Role,
} from "../rbac/permissions";
import type { PageKey } from "../rbac/pages";
import { setApiSession } from "../api/client";
import { registerSignOut } from "@/compat/next-auth-react";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";
export type PermissionsStatus = "idle" | "loading" | "ready" | "error";

export interface AuthContextValue {
  status: AuthStatus;
  user: MobileUser | null;
  role: Role | null;
  /** null until loaded. `null` is NOT "denied" — see `route-guard.ts`. */
  permissions: Record<string, PagePermissionRow> | null;
  permissionsStatus: PermissionsStatus;
  visiblePageKeys: PageKey[];
  /** Set when sign-in worked but the Auth.js cookie bridge did not. */
  degradedMessage: string | null;
  login(email: string, password: string): Promise<void>;
  /**
   * Sign in with a Google ID token. Rejects with an `AuthError` whose `code`
   * is `access_request_pending` / `account_disabled` / `invalid_token` /
   * `google_disabled` — the caller routes on those rather than on the message.
   */
  loginWithGoogle(credential: string): Promise<void>;
  /**
   * Wave 3 (agent W12, auth hardening) — finishes a login that was
   * interrupted by `login()` rejecting with `AuthError('mfa_required')`.
   * Mirrors `loginWithGoogle` exactly on purpose: same shape, same reason
   * (the token pair alone does not update `status`/`user` — `AuthSession` has
   * no `onChange` wired up here, so this provider's own state has to be
   * pushed forward explicitly on every path that can produce a session).
   *
   * FLAGGED PER THE WAVE BRIEF: this is the one AuthProvider change in this
   * wave beyond a one-line flag read. It was unavoidable — without it,
   * completing an MFA challenge would write a valid session to storage while
   * the app kept rendering "unauthenticated".
   */
  completeMfaLogin(mfaToken: string, code: string): Promise<void>;
  logout(): Promise<void>;
  reloadPermissions(): Promise<void>;
  /** `fetch` with the bearer attached and one 401 healed by a rotation. */
  authorizedFetch(input: string, init?: RequestInit): Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Where the current user's own `PagePermission` rows come from.
 *
 * **This endpoint does not exist yet.** Nothing in `src/app/api/` or
 * `backend/api/app/routers/` serves a user their own permission rows —
 * `/api/admin/permissions/{userId}` is admin-only, and the Next.js app never
 * needed a route because its layout read Prisma directly. Building it is
 * backend work (`/py-api/v1/permissions/me` under D-001) and is the single
 * hand-off S1 owes S2.
 *
 * Until it lands: ADMINs are unaffected (they short-circuit before any lookup,
 * exactly as `resolvePermission` does server-side), and a USER sees an
 * explicit "couldn't load your access settings — retry" screen rather than a
 * silently wrong nav or a false denial.
 */
const PERMISSIONS_URL: string =
  import.meta.env?.VITE_PERMISSIONS_URL ?? "/py-api/v1/permissions/me";

export interface AuthProviderProps {
  children: ReactNode;
  /** Injectable for tests; the app builds its own from browser storage. */
  session?: AuthSession;
}

function makeSession(): AuthSession {
  return new AuthSession({
    storage: createBrowserTokenStorage(),
    deviceId: getOrCreateDeviceId(),
    deviceName: describeBrowser(),
  });
}

export function AuthProvider({ children, session: injected }: AuthProviderProps) {
  const sessionRef = useRef<AuthSession | null>(injected ?? null);
  if (sessionRef.current === null) sessionRef.current = makeSession();
  const session = sessionRef.current;

  // Register the session for `apiFetch`, which copied components reach without
  // going through React context. Done during render (not in an effect) so a
  // `queryFn` that fires on the very first commit already has a bearer.
  setApiSession(session);

  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<MobileUser | null>(null);
  const [permissions, setPermissions] = useState<Record<string, PagePermissionRow> | null>(null);
  const [permissionsStatus, setPermissionsStatus] = useState<PermissionsStatus>("idle");
  const [degradedMessage, setDegradedMessage] = useState<string | null>(null);

  const role: Role | null = user ? (user.role as Role) : null;

  /**
   * ADMIN needs no request: `ADMIN_FULL_GRANT` is the same constant the server
   * uses, and `resolvePermission` short-circuits on role before reading a row.
   * Only a USER actually costs a round-trip.
   */
  const loadPermissions = useCallback(
    async (forRole: Role) => {
      if (forRole === "ADMIN") {
        setPermissions(ADMIN_FULL_GRANT);
        setPermissionsStatus("ready");
        return;
      }
      setPermissionsStatus("loading");
      try {
        const res = await session.authorizedFetch(PERMISSIONS_URL, {
          headers: { accept: "application/json" },
        });
        if (!res.ok) {
          setPermissions(null);
          setPermissionsStatus("error");
          return;
        }
        setPermissions(normalizePermissionRows(await res.json()));
        setPermissionsStatus("ready");
      } catch {
        setPermissions(null);
        setPermissionsStatus("error");
      }
    },
    [session],
  );

  // Restore a persisted session exactly once, at startup.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const restored = await session.hydrate();
      if (cancelled) return;
      if (restored && session.isSignedIn()) {
        setUser(restored.user);
        setStatus("authenticated");
        void loadPermissions(restored.user.role as Role);
      } else {
        setStatus("unauthenticated");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, loadPermissions]);

  const login = useCallback(
    async (email: string, password: string) => {
      const next = await session.login(email, password);

      // The token pair is the real session; the Auth.js cookie is the v2.2
      // compatibility crutch for the un-ported `/api/*` routes. A bridge
      // failure degrades, it does not block.
      let bridge: LegacyBridgeResult = { outcome: "disabled", message: null };
      try {
        bridge = await establishLegacySession(email, password);
      } catch {
        bridge = {
          outcome: "unavailable",
          message: "Signed in, but the legacy pages could not be reached.",
        };
      }
      setDegradedMessage(bridge.message);

      setUser(next.user);
      setStatus("authenticated");
      await loadPermissions(next.user.role as Role);
    },
    [session, loadPermissions],
  );

  /**
   * The Google path, deliberately shorter than `login` above.
   *
   * No legacy Auth.js bridge: that shim replays the PASSWORD against Next.js's
   * cookie endpoint, and there is no password here to replay. It is also on
   * its way out — every domain in `API_DOMAINS` is on FastAPI as of CUT-02 —
   * so Google sign-in is written for the world after the bridge rather than
   * inheriting a crutch it cannot use.
   */
  const loginWithGoogle = useCallback(
    async (credential: string) => {
      const next = await session.loginWithGoogle(credential);
      setDegradedMessage(null);
      setUser(next.user);
      setStatus("authenticated");
      await loadPermissions(next.user.role as Role);
    },
    [session, loadPermissions],
  );

  /**
   * Wave 3 (W12) — see the interface doc above for why this exists at all.
   * No legacy bridge here either, for the same reason as `loginWithGoogle`:
   * there is no plaintext password at this point to replay against Next.js
   * (only the short-lived `mfaToken` + the code), and every domain is on
   * FastAPI regardless.
   */
  const completeMfaLogin = useCallback(
    async (mfaToken: string, code: string) => {
      const next = await session.completeMfaLogin(mfaToken, code);
      setDegradedMessage(null);
      setUser(next.user);
      setStatus("authenticated");
      await loadPermissions(next.user.role as Role);
    },
    [session, loadPermissions],
  );

  const logout = useCallback(async () => {
    // Revoke server-side FIRST — a cleared browser must be a dead token, not
    // just a forgotten one (see the storage note in `token-storage.ts`).
    await session.logout();
    // Without this, Google can auto-select the account the user just signed
    // out of and put them straight back in — which reads as a broken sign-out.
    disableGoogleAutoSelect();
    await clearLegacySession();
    setUser(null);
    setPermissions(null);
    setPermissionsStatus("idle");
    setDegradedMessage(null);
    setStatus("unauthenticated");
  }, [session]);

  // Publish `logout` to the `next-auth/react` shim, so the copied components
  // that call `signOut({ callbackUrl: "/login" })` keep working untouched.
  useEffect(() => {
    registerSignOut(logout);
    return () => registerSignOut(null);
  }, [logout]);

  const reloadPermissions = useCallback(async () => {
    if (role) await loadPermissions(role);
  }, [role, loadPermissions]);

  /**
   * A 401 that survives one rotation means the session is genuinely dead —
   * drop it so the guard sends the user to `/login` instead of leaving the
   * shell rendering empty widgets forever.
   */
  const authorizedFetch = useCallback(
    async (input: string, init?: RequestInit) => {
      try {
        return await session.authorizedFetch(input, init);
      } catch (err) {
        if (
          err instanceof AuthError &&
          (err.code === "no_session" || err.code === "refresh_failed")
        ) {
          setUser(null);
          setPermissions(null);
          setPermissionsStatus("idle");
          setStatus("unauthenticated");
        }
        throw err;
      }
    },
    [session],
  );

  const visiblePageKeys = useMemo(
    () => (role ? computeVisiblePageKeys(role, permissions) : []),
    [role, permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      role,
      permissions,
      permissionsStatus,
      visiblePageKeys,
      degradedMessage,
      login,
      loginWithGoogle,
      completeMfaLogin,
      logout,
      reloadPermissions,
      authorizedFetch,
    }),
    [
      status,
      user,
      role,
      permissions,
      permissionsStatus,
      visiblePageKeys,
      degradedMessage,
      login,
      loginWithGoogle,
      completeMfaLogin,
      logout,
      reloadPermissions,
      authorizedFetch,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
